import { useQuery } from '@apollo/client';
import { PlayIcon } from '@heroicons/react/24/solid';
import { Button } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Reel, ReelsApprovalArgs, ReelsApprovalResult, ReelsApprovalSort } from '../../graphql/graphql';
import { USER_APPROVED_REELS } from '../../graphql/queries-gql';

const Container = styled.section`
  min-width: 0;
  padding: 12px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

const Title = styled.h3`
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.textColor};
`;

const Message = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.secondaryTextColor};
`;

const Gallery = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 3px 3px 8px;
`;

const Thumbnail = styled.button`
  position: relative;
  flex: 0 0 80px;
  aspect-ratio: 9 / 16;
  padding: 0;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.borderStrong};
  border-radius: 6px;
  background: ${({ theme }) => theme.surfaceRaised};
  color: ${({ theme }) => theme.textColor};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  svg {
    position: absolute;
    width: 24px;
    height: 24px;
    bottom: 6px;
    right: 6px;
    padding: 4px;
    border-radius: 50%;
    color: white;
    background: rgba(0, 0, 0, 0.65);
  }
`;

type Props = {
  userId: string;
  currentReelId: string;
  onReelClick: (reel: Reel) => void;
};

const UserReelHistory = ({ userId, currentReelId, onReelClick }: Props) => {
  const containerRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const { data, loading, error, refetch } = useQuery<
    { reelsApproval: Pick<ReelsApprovalResult, 'searchedReels'> },
    { args: ReelsApprovalArgs }
  >(USER_APPROVED_REELS, {
    variables: { args: { search: userId, sort: ReelsApprovalSort.Newest } },
    skip: !visible,
  });
  const history = (data?.reelsApproval.searchedReels ?? []).filter(
    (reel) => reel.byUserId === userId && reel._id !== currentReelId
  );

  return (
    <Container ref={containerRef} aria-label="User reel history">
      <Title>
        Previously approved reels{visible && !loading && !error ? ` (${history.length})` : ''}
      </Title>
      {!visible || loading ? (
        <Message role="status">Loading previous reels...</Message>
      ) : error ? (
        <div role="alert">
          <Message>Could not load previous reels.</Message>
          <Button size="small" onClick={() => { void refetch().catch(() => {}); }}>Retry</Button>
        </div>
      ) : history.length === 0 ? (
        <Message>No previously approved reels.</Message>
      ) : (
        <>
          <Gallery aria-label="Previously approved reels">
            {history.map((reel, index) => (
              <Thumbnail
                key={reel._id}
                type="button"
                aria-label={`Play previous reel ${index + 1}`}
                title={`Play previous reel ${index + 1}`}
                onClick={() => onReelClick(reel)}
              >
                {reel.thumbnailUrl && <img src={reel.thumbnailUrl} alt="" loading="lazy" />}
                <PlayIcon aria-hidden="true" />
              </Thumbnail>
            ))}
          </Gallery>
          {history.length >= 200 && <Message>Showing the 200 most recent approved reels.</Message>}
        </>
      )}
    </Container>
  );
};

export default UserReelHistory;
